// src/contexts/LocationContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import countriesRegions from '../utils/countriesRegions'

type BaseBlogRegion = 'global' | 'africa' | 'europe'

interface LocationData {
  countryCode: string
  country_name?: string
  city?: string
  currency?: string
  continent_code?: string
  blogRegion: BaseBlogRegion
  isLoadingLocation?: boolean
  error?: Error
  setCountryCode: (countryCode: string) => void
}

const DEFAULT_COUNTRY = 'US'
const COUNTRY_STORAGE_KEY = 'rentaflix_country_code_v1'

const normalizeCountryCode = (value?: string | null): string | null => {
  if (!value) return null
  const code = value.toUpperCase().trim()
  return /^[A-Z]{2}$/.test(code) ? code : null
}

const getBaseBlogRegionByCountry = (countryCode: string): BaseBlogRegion => {
  const entry = countriesRegions.find(item => item.countryCode === countryCode)
  if (!entry?.region) return 'global'

  const normalizedRegion = entry.region.toLowerCase()
  if (normalizedRegion.startsWith('africa')) return 'africa'
  if (normalizedRegion.startsWith('europe')) return 'europe'
  return 'global'
}

const getCountryFromLang = (): string => {
  if (typeof navigator === 'undefined') return DEFAULT_COUNTRY
  const lang = navigator.language || navigator.languages?.[0] || ''
  const parts = lang.split('-')
  const code = parts.length === 2 ? parts[1].toUpperCase() : ''
  return /^[A-Z]{2}$/.test(code) ? code : DEFAULT_COUNTRY
}

const getStoredCountry = (): string | null => {
  if (typeof window === 'undefined') return null
  return normalizeCountryCode(window.localStorage.getItem(COUNTRY_STORAGE_KEY))
}

const LocationContext = createContext<LocationData>({
  countryCode: DEFAULT_COUNTRY,
  country_name: 'United States',
  currency: 'USD',
  continent_code: 'NA',
  blogRegion: getBaseBlogRegionByCountry(DEFAULT_COUNTRY),
  setCountryCode: () => undefined
})

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialCountry = getStoredCountry() || getCountryFromLang()
  const [location, setLocation] = useState<Omit<LocationData, 'isLoadingLocation'>>({
    countryCode: initialCountry,
    country_name: 'United States',
    currency: 'USD',
    continent_code: 'NA',
    blogRegion: getBaseBlogRegionByCountry(initialCountry),
    setCountryCode: () => undefined
  })
  const [isLoadingLocation, setIsLoadingLocation] = useState(true)
  const [error, setError] = useState<Error>()

  const setCountryCode = useCallback((countryCode: string) => {
    const normalized = normalizeCountryCode(countryCode)
    if (!normalized) return

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, normalized)
    }

    setLocation(previous => ({
      ...previous,
      countryCode: normalized,
      blogRegion: getBaseBlogRegionByCountry(normalized)
    }))
  }, [])

  useEffect(() => {
    const detectCountry = async () => {
      const fallbackCountry = getCountryFromLang()
      const storedCountry = getStoredCountry()

      // Use cached selection first and skip remote lookup to avoid post-load UI retrigger.
      if (storedCountry) {
        setLocation(previous => ({
          ...previous,
          countryCode: storedCountry,
          blogRegion: getBaseBlogRegionByCountry(storedCountry),
          setCountryCode
        }))
        setIsLoadingLocation(false)
        return
      }

      try {
        const res = await fetch('https://ipapi.co/json/')
        const data = await res.json()

        const apiCountry = normalizeCountryCode(data?.country)

        if (apiCountry) {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(COUNTRY_STORAGE_KEY, apiCountry)
          }

          setLocation({
            countryCode: apiCountry,
            country_name: data.country_name || 'Unknown Country',
            city: data.city,
            continent_code: data.continent_code || 'NA',
            currency: data.currency || 'USD',
            blogRegion: getBaseBlogRegionByCountry(apiCountry),
            setCountryCode
          })
        } else {
          setLocation({
            countryCode: fallbackCountry,
            blogRegion: getBaseBlogRegionByCountry(fallbackCountry),
            setCountryCode
          })
        }
      } catch (err) {
        console.warn('Geolocation failed, falling back to browser language:', err)
        setError(err as Error)
        setLocation({
          countryCode: fallbackCountry,
          blogRegion: getBaseBlogRegionByCountry(fallbackCountry),
          setCountryCode
        })
      } finally {
        setIsLoadingLocation(false)
      }
    }

    detectCountry()
  }, [setCountryCode])

  const contextValue = useMemo(
    () => ({
      ...location,
      setCountryCode,
      isLoadingLocation,
      error
    }),
    [location, setCountryCode, isLoadingLocation, error]
  )

  return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>
}

export const useLocation = () => useContext(LocationContext)